import type { ExperimentPhase } from '~/features/experiments/types/experiment.types';

/** 0–5 covers the content-enrichment funnel's 6 visible steps. */
export type FunnelStepIndex = 0 | 1 | 2 | 3 | 4 | 5;

/**
 * Named step indices — the single source of truth for the funnel's ordering.
 * Use these instead of bare integers (panel-content switch, CTA/poll gating,
 * back-nav) so a future renumber is a one-line change here rather than a hunt
 * across files for magic `=== 2` literals that silently desync.
 */
export const FUNNEL_STEP = {
  CURRENT_SCORE: 0,
  REVIEW: 1,
  FULL_CATALOG: 2,
  OVERVIEW: 3,
  GROUPS: 4,
  DELIVERY: 5,
} as const satisfies Record<string, FunnelStepIndex>;

/**
 * Funnel path templates keyed by step index. Index 0 is the layout's index
 * route (no suffix). Kept as a const-typed map so the layout's
 * `navigate({ to })` calls remain type-safe against the route tree.
 *
 * Step order (post the combined Sample + Score loading screen):
 *   0 Current Content Score · 1 Review & Refine · 2 Full Catalog Generation
 *   3 Enrichment Overview · 4 Review Groups & Outliers · 5 Delivery
 *
 * NB: the per-page URL segments are unchanged — only which step index maps to
 * each segment moved when Review Groups & Outliers was relocated to just before
 * Delivery. `resolveStepIndex` derives the inverse from this map, so the two
 * stay in sync automatically on any renumber.
 */
export const FUNNEL_PATH_BY_STEP = {
  0: '/experiments/$expId/content-enrichment',
  1: '/experiments/$expId/content-enrichment/review',
  2: '/experiments/$expId/content-enrichment/full',
  3: '/experiments/$expId/content-enrichment/overview',
  4: '/experiments/$expId/content-enrichment/groups',
  5: '/experiments/$expId/content-enrichment/delivery',
} as const;

export type FunnelPath = (typeof FUNNEL_PATH_BY_STEP)[FunnelStepIndex];

/** Every funnel step, in order — derived from {@link FUNNEL_PATH_BY_STEP} so a
 *  renumber can't leave a step out. The layout uses this to make the whole
 *  stepper navigable; callers that collapse a step (see `hasSplit`) filter it. */
export const ALL_FUNNEL_STEP_INDICES: readonly FunnelStepIndex[] = Object.keys(FUNNEL_PATH_BY_STEP)
  .map(Number)
  .sort((a, b) => a - b) as FunnelStepIndex[];

/**
 * Nearest **visible** step in `direction` (-1 back, +1 forward), or `null` past
 * either end of the funnel. Drives the footer's Back/Continue targets.
 *
 * Hops over Review Groups & Outliers when `hasSplit` is false: Meta and the
 * scheduled flows collapse that step out of the stepper, so stepping 5 → 4 there
 * would land on a step the operator can't see and the guard has no opinion about.
 */
export function adjacentVisibleStep(
  from: number,
  direction: -1 | 1,
  hasSplit: boolean,
): FunnelStepIndex | null {
  const next = from + direction;
  const adjusted = !hasSplit && next === FUNNEL_STEP.GROUPS ? next + direction : next;
  if (adjusted < FUNNEL_STEP.CURRENT_SCORE || adjusted > FUNNEL_STEP.DELIVERY) return null;
  return adjusted as FunnelStepIndex;
}

/** Shared prefix of every funnel path; the per-step suffix is what we match on. */
const CONTENT_ENRICHMENT_PREFIX = '/experiments/$expId/content-enrichment';

/**
 * URL pathname → funnel step index, DERIVED from {@link FUNNEL_PATH_BY_STEP} so
 * the path→index inverse can never drift from the index→path map on a renumber.
 * Checks deepest suffixes first; step 0 is the bare prefix (the fallback).
 */
export function resolveStepIndex(pathname: string): FunnelStepIndex {
  for (const step of [5, 4, 3, 2, 1] as const) {
    const suffix = FUNNEL_PATH_BY_STEP[step].slice(CONTENT_ENRICHMENT_PREFIX.length);
    if (pathname.endsWith(suffix)) return step;
  }
  return 0;
}

/**
 * The early "review region" — Current Content Score (0) and Review & Refine
 * (1). The backend collapses both onto a single `REVIEW_AND_REFINE` phase, so
 * the guard permits free navigation between them; only the "Generate Full
 * Catalog" trigger (Review & Refine) advances the phase to
 * `FULL_CATALOG_GENERATION`. Sample + scoring both finished before the stepper
 * appeared, so both steps have data on entry.
 */
export const REVIEW_REGION_STEP_INDICES: readonly number[] = [
  FUNNEL_STEP.CURRENT_SCORE,
  FUNNEL_STEP.REVIEW,
];

/**
 * The post-generation "overview region" — Enrichment Overview (3) and Review
 * Groups & Outliers (4). Both sit under the single `ENRICHMENT_OVERVIEW` phase:
 * the pipeline pauses at AWAITING_APPROVAL while the operator reviews results,
 * then reviews groups/outliers and launches. The guard permits free navigation
 * between them; only the Launch Experiment approval (Review Groups & Outliers)
 * advances the phase to `LAUNCH`. The full-catalog pipeline finished before
 * either step is reachable, so both have data on entry.
 */
export const OVERVIEW_REGION_STEP_INDICES: readonly number[] = [
  FUNNEL_STEP.OVERVIEW,
  FUNNEL_STEP.GROUPS,
];

/**
 * Free-navigation regions: contiguous UI steps that share one backend phase and
 * so allow back/forward movement without the guard redirecting. Each region's
 * entry (its lowest index) is the canonical phase target; the operator walks
 * forward to the later step(s) via the Continue CTAs.
 */
const FREE_NAV_REGIONS: readonly (readonly number[])[] = [
  REVIEW_REGION_STEP_INDICES,
  OVERVIEW_REGION_STEP_INDICES,
];

/** True when both steps belong to the same free-navigation region. */
export function inSameFreeNavRegion(a: number, b: number): boolean {
  return FREE_NAV_REGIONS.some((region) => region.includes(a) && region.includes(b));
}

/**
 * True when the step belongs to any free-navigation region — i.e. its backend
 * phase collapses multiple UI steps, so the layout drives the stepper's progress
 * marks off the URL and records a resume point while the operator walks it.
 */
export function isFreeNavRegionStep(step: number): boolean {
  return FREE_NAV_REGIONS.some((region) => region.includes(step));
}

/**
 * The stepper's progress marker — drives the green completion checks
 * (`index < progress`) and the accent highlight (`index === progress`).
 *
 * Outside a free-nav region it's the canonical (phase) step, so the marks stay
 * stable while the operator backward-inspects earlier steps. INSIDE a region it
 * advances with the URL as the operator walks forward (e.g. Enrichment Overview →
 * Review Groups & Outliers) — but is clamped to never drop BELOW the canonical
 * step. Without the clamp, clicking back from a region step to an earlier
 * completed step (e.g. Overview → Review & Refine) regressed every prior step's
 * check to a pending circle and pulled the accent off the real current step,
 * making the funnel look stuck on a previous step.
 */
export function resolveProgressStepIndex(
  currentStepIndex: number,
  effectiveCanonicalStep: FunnelStepIndex | null,
  inFreeNavRegion: boolean,
): number {
  if (inFreeNavRegion && effectiveCanonicalStep !== null) {
    return Math.max(currentStepIndex, effectiveCanonicalStep);
  }
  return effectiveCanonicalStep ?? currentStepIndex;
}

/**
 * Steps the operator can backward-visit read-only once past them: the two early
 * review steps (Current Content Score, Review & Refine) plus the two
 * post-generation review steps (Enrichment Overview, Review Groups & Outliers).
 * The two polling screens (Full Catalog 2, Delivery 5) stay out — there's no
 * useful read-only view of in-flight progress.
 */
export const INSPECTABLE_STEP_INDICES: readonly number[] = [
  FUNNEL_STEP.CURRENT_SCORE,
  FUNNEL_STEP.REVIEW,
  FUNNEL_STEP.OVERVIEW,
  FUNNEL_STEP.GROUPS,
];

/**
 * Polling/progress screens — Full Catalog (2) and Delivery (5). Each polls
 * its job status and auto-advances on completion, so they own their own
 * forward motion. The funnel guard must NOT redirect off them: when
 * `experimentPhase` races ahead (or lags behind) as the user lands, a
 * redirect would skip the progress screen entirely. Exempting them lets the
 * page render, poll, then self-advance.
 */
export const PROGRESS_STEP_INDICES: readonly number[] = [
  FUNNEL_STEP.FULL_CATALOG,
  FUNNEL_STEP.DELIVERY,
];

/**
 * Progress screens that become backward-visitable read-only ONCE their pipeline
 * finishes (the phase has advanced past them) — currently just Full Catalog
 * Generation. Unlike {@link INSPECTABLE_STEP_INDICES} (the always-revisitable
 * review steps), these are live auto-advancing screens on first visit; only
 * after completion is there a useful read-only view — the stamped stage timeline
 * with per-stage durations. The page suppresses its auto-advance on such a
 * revisit and shows a "pick a later step" hint, so the guard no longer needs to
 * bounce the operator forward.
 */
export const REVISITABLE_PROGRESS_STEP_INDICES: readonly number[] = [FUNNEL_STEP.FULL_CATALOG];

/** What the funnel layout should do for the current (phase, URL-step) pair. */
export type FunnelGuardAction =
  | { type: 'stay' }
  | { type: 'exit' }
  | { type: 'redirect'; to: FunnelStepIndex };

/**
 * Pure decision for the funnel guard — kept out of the layout's `useEffect`
 * so it's unit-testable. Precedence:
 *   1. phase unset → `stay` (no enforcement; the funnel's own gates take over).
 *   2. phase past the funnel → `exit`.
 *   3. **cold resume within a free-nav region** — on initial entry, if a
 *      `resumeStepIndex` (the furthest step previously reached in the current
 *      region) sits ahead of the region entry, `redirect` there (or `stay` if
 *      already on it). The backend collapses each region's steps onto one phase
 *      (0/1 → REVIEW_AND_REFINE, 3/4 → ENRICHMENT_OVERVIEW), so without this
 *      every resume snaps to the region entry; this lands the operator back
 *      where they left off (new tab / re-select from the list). Only fires
 *      within a region — once the phase advances out, the phase target wins.
 *   4. already on the canonical step → `stay`.
 *   5. **initial entry** (cold resume / deep-link) and not on the canonical
 *      step → `redirect`. A fresh landing pins to the active step (the start
 *      of the review region for review phases); the allowances below are for
 *      navigation WITHIN an already-open funnel.
 *   6. **free-nav region** — the URL step and the canonical step are in the
 *      same region → `stay`. Lets the operator walk the early review steps
 *      (Current Content Score → Review & Refine) or the post-generation steps
 *      (Enrichment Overview → Review Groups & Outliers) freely; only the
 *      region's terminal trigger (pipeline / launch) advances the phase out.
 *   7. on a progress/polling screen → `stay` (it self-advances).
 *   8. on a progress screen's immediate successor while the phase still points
 *      at that progress screen → `stay` (the screen auto-advanced but the
 *      experiment record / phase lags one step behind).
 *   9. backward visit onto a *revisitable* progress screen whose pipeline has
 *      finished (Full Catalog Generation) → `stay` (read-only inspection of the
 *      completed timeline; the page suppresses its own auto-advance there).
 *   10. backward visit to an inspectable completed review step → `stay` (read-only).
 *   11. otherwise (forward overshoot, or backward onto a non-revisitable
 *      progress screen) → `redirect` to the canonical step.
 */
export function resolveFunnelGuardAction(
  phaseTarget: FunnelStepIndex | 'exit' | null,
  currentStepIndex: number,
  isInitialEntry = false,
  resumeStepIndex: FunnelStepIndex | null = null,
): FunnelGuardAction {
  if (phaseTarget === null) return { type: 'stay' };
  if (phaseTarget === 'exit') return { type: 'exit' };
  // Cold resume within a free-nav region takes precedence over the snap-to-
  // entry below: phaseTarget is always the region entry, so honour the furthest
  // step the operator previously reached within that same region.
  if (
    isInitialEntry &&
    typeof resumeStepIndex === 'number' &&
    inSameFreeNavRegion(phaseTarget, resumeStepIndex) &&
    resumeStepIndex > phaseTarget
  ) {
    return resumeStepIndex === currentStepIndex
      ? { type: 'stay' }
      : { type: 'redirect', to: resumeStepIndex };
  }
  if (phaseTarget === currentStepIndex) return { type: 'stay' };
  // Cold entry: land on the active step regardless of where the URL points.
  if (isInitialEntry) return { type: 'redirect', to: phaseTarget };
  // Free navigation within a phase-collapsed region (early review 0/1, or the
  // post-generation overview/groups 3/4).
  if (inSameFreeNavRegion(currentStepIndex, phaseTarget)) {
    return { type: 'stay' };
  }
  // Progress screens (Full Catalog, Delivery) self-advance, so STAY when the
  // operator is on or ahead of one — that covers forward navigation onto it and
  // the phase lagging behind.
  if (PROGRESS_STEP_INDICES.includes(currentStepIndex) && currentStepIndex >= phaseTarget) {
    return { type: 'stay' };
  }
  // Progress screen auto-advanced to its successor; the phase lags behind.
  if (PROGRESS_STEP_INDICES.includes(phaseTarget) && currentStepIndex === phaseTarget + 1) {
    return { type: 'stay' };
  }
  // Backward visit onto a *revisitable* progress screen whose pipeline has
  // finished (Full Catalog Generation, phase already past it): STAY so the
  // operator can inspect the completed stage timeline + durations. The page
  // suppresses its auto-advance on this revisit, so it no longer yanks them
  // forward — the historical reason this case used to redirect.
  if (
    REVISITABLE_PROGRESS_STEP_INDICES.includes(currentStepIndex) &&
    currentStepIndex < phaseTarget
  ) {
    return { type: 'stay' };
  }
  if (currentStepIndex < phaseTarget && INSPECTABLE_STEP_INDICES.includes(currentStepIndex)) {
    return { type: 'stay' };
  }
  return { type: 'redirect', to: phaseTarget };
}

/**
 * Resolve what the funnel layout should do for a given experimentPhase:
 *   - The review phases (`CURRENT_CONTENT_SCORE`, `GENERATE_SAMPLE_CONTENT`,
 *     `REVIEW_AND_REFINE`) all map to step 0 — the entry of the early review
 *     region. The layout permits forward navigation to step 1 (see
 *     {@link REVIEW_REGION_STEP_INDICES}) and drives the per-step progress
 *     marks off the URL while in the region. Sample enrichment now runs during
 *     the combined loading screen that precedes the stepper, so
 *     `GENERATE_SAMPLE_CONTENT` no longer owns a dedicated step.
 *   - `FULL_CATALOG_GENERATION` → 2 (Full Catalog Generation).
 *   - `ENRICHMENT_OVERVIEW` → 3 — the entry of the post-generation overview
 *     region (Enrichment Overview 3 → Review Groups & Outliers 4). The operator
 *     reviews results, picks an outlier sensitivity, then launches; the layout
 *     permits free navigation across 3/4 (see {@link OVERVIEW_REGION_STEP_INDICES}).
 *   - `LAUNCH` → 5 (Delivery). After the operator launches on Review Groups &
 *     Outliers, the pipeline runs delivery and the user watches it on the
 *     in-funnel Delivery step.
 *   - `LIVE` → `'exit'` (delivery finished); redirect to `/experiments/$id`.
 *   - `null` → phase unset (pre-trigger / legacy); render whatever the URL says.
 */
export function resolvePhaseTarget(
  phase: ExperimentPhase | null | undefined,
): FunnelStepIndex | 'exit' | null {
  if (phase == null) return null;
  switch (phase) {
    case 'CURRENT_CONTENT_SCORE':
    case 'GENERATE_SAMPLE_CONTENT':
    case 'REVIEW_AND_REFINE':
      return FUNNEL_STEP.CURRENT_SCORE;
    case 'FULL_CATALOG_GENERATION':
      return FUNNEL_STEP.FULL_CATALOG;
    case 'ENRICHMENT_OVERVIEW':
      return FUNNEL_STEP.OVERVIEW;
    case 'LAUNCH':
      return FUNNEL_STEP.DELIVERY;
    case 'LIVE':
      return 'exit';
    default:
      // Unknown value (forward-compat) — don't enforce so we never trap the
      // operator on a screen they can't leave.
      return null;
  }
}

/**
 * True when the operator is *revisiting* a finished Full Catalog Generation —
 * the canonical phase has already advanced past it — rather than watching it run
 * live. The full-catalog page captures this at mount to suppress its auto-advance
 * and surface the "pick a later step" hint instead of bouncing the operator back
 * to the current step. Takes the already-resolved progress step (output of
 * {@link resolvePhaseTarget} / the context's `phaseToStep`).
 */
export function isFullCatalogRevisit(progressStep: FunnelStepIndex | 'exit' | null): boolean {
  return (
    progressStep === 'exit' ||
    (typeof progressStep === 'number' && progressStep > FUNNEL_STEP.FULL_CATALOG)
  );
}
